import { ComponentProps, ReactNode } from 'react';
import Icon from '@amzn/meridian/icon';
import { css, cx, keyframes } from '@emotion/css';
import useActionButtonStyles from '../hooks/useActionButtonStyles';
import { reducedMotionQuery } from '../theme/motion';

const spinKeyframes = keyframes({
  to: { transform: 'rotate(360deg)' },
});

const iconSlotClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
});

const spinningIconClass = css({
  animation: `${spinKeyframes} 0.9s linear infinite`,
  [reducedMotionQuery]: { animation: 'none' },
});

const spinnerRingClass = css({
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: '2px solid currentColor',
  borderRightColor: 'transparent',
  animation: `${spinKeyframes} 0.7s linear infinite`,
  [reducedMotionQuery]: { animation: 'none', borderRightColor: 'currentColor', opacity: 0.6 },
});

type ActionButtonVariant = 'primary' | 'secondary' | 'tertiary';

interface ActionButtonProps {
  variant: ActionButtonVariant;
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  size?: 'medium' | 'small';
  iconTokens?: ComponentProps<typeof Icon>['tokens'];
  // "spin-icon" rotates the icon (suited to refresh), "spinner" swaps the icon for a ring.
  busyIndicator?: 'spin-icon' | 'spinner';
  busy?: boolean;
  disabled?: boolean;
}

export default function ActionButton({
  variant,
  children,
  onClick,
  type = 'button',
  size = 'medium',
  iconTokens,
  busyIndicator = 'spinner',
  busy = false,
  disabled = false,
}: ActionButtonProps) {
  const { primaryButtonClass, secondaryButtonClass, tertiaryButtonClass, smallButtonClass } = useActionButtonStyles();
  const variantClassByName: Record<ActionButtonVariant, string> = {
    primary: primaryButtonClass,
    secondary: secondaryButtonClass,
    tertiary: tertiaryButtonClass,
  };
  const showSpinnerRing = busy && busyIndicator === 'spinner';
  const spinIcon = busy && busyIndicator === 'spin-icon';

  return (
    <button
      type={type}
      className={cx(variantClassByName[variant], size === 'small' && smallButtonClass)}
      onClick={onClick}
      disabled={disabled}
      aria-busy={busy || undefined}
    >
      {(iconTokens || showSpinnerRing) && (
        <span className={`${iconSlotClass} ${spinIcon ? spinningIconClass : ''}`} aria-hidden="true">
          {showSpinnerRing ? <span className={spinnerRingClass} /> : iconTokens && <Icon tokens={iconTokens} />}
        </span>
      )}
      {children}
    </button>
  );
}
